// ============================================================================
// Public Catalog Types
// ============================================================================

/**
 * Hero section of the catalog
 */
export interface CatalogHeroSection {
  logo: string | null;
  banner: string | null;
  heroMessage: string | null;
  ctaButtonText: string;
  whatsappUrl: string | null;
}

/**
 * Activity/class offered by the gym
 */
export interface CatalogActivity {
  id: string;
  name: string;
  description: string | null;
  startTime: string;
  duration: number;
  days: string[];
  imageUrl: string | null;
}

/**
 * Membership plan offered by the gym
 */
export interface CatalogPlan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  duration: string;
  features: any;
  whatsappUrl: string | null;
}

/**
 * Social media links
 */
export interface CatalogSocialMedia {
  instagram: string | null;
  facebook: string | null;
  tiktok: string | null;
  whatsapp: string | null;
}

/**
 * Gym location information
 */
export interface CatalogLocation {
  address: string | null;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
}

/**
 * Formatted schedule for a day
 */
export interface FormattedSchedule {
  day: string;
  hours: string;
}

/**
 * Complete catalog response
 */
export interface CatalogResponse {
  gym: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    phone: string | null;
    email: string | null;
    currency: string;
  };
  hero: CatalogHeroSection;
  plans: CatalogPlan[];
  activities: CatalogActivity[];
  schedule: FormattedSchedule[];
  socialMedia: CatalogSocialMedia;
  location: CatalogLocation;
}

/**
 * City with gym count
 */
export interface CityWithGyms {
  city: string;
  state: string;
  count: number;
}

/**
 * Search catalog parameters
 */
export interface SearchCatalogParams {
  search?: string;
  city?: string;
  state?: string;
  latitude?: string;
  longitude?: string;
  radius?: string;
  limit?: number;
  offset?: number;
}

/**
 * Catalog gym in search results
 */
export interface CatalogGym {
  id: string;
  name: string;
  slug: string;
  address?: string;
  city?: string;
  state?: string;
  phone?: string;
  email?: string;
  amenities?: any;
  settings?: any;
}

/**
 * Search catalog response
 */
export interface SearchCatalogResponse {
  gyms: CatalogGym[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// ============================================================================
// Lead Management Types
// ============================================================================

/**
 * Lead gender enum
 */
export enum LeadGender {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

/**
 * Create lead DTO
 */
export interface CreateLeadDto {
  fullName: string;
  age: number;
  phone: string;
  gender: LeadGender;
  objective: string;
}

/**
 * Catalog lead entity
 */
export interface CatalogLead {
  id: string;
  catalogId: string;
  fullName: string;
  age: number;
  phone: string;
  gender: string;
  objective: string;
  source: string;
  ipAddress: string | null;
  userAgent: string | null;
  isContacted: boolean;
  contactedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Lead filters for querying
 */
export interface LeadFiltersDto {
  startDate?: string;
  endDate?: string;
  isContacted?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Lead submission response
 */
export interface LeadSubmissionResponse {
  message: string;
}

// ============================================================================
// Admin Catalog Configuration Types
// ============================================================================

/**
 * Gym catalog configuration
 */
export interface GymCatalog {
  id: string;
  gymId: string;
  slug: string;
  logo: string | null; // Deprecated: kept for backward compatibility
  banner: string | null; // Deprecated: kept for backward compatibility
  logoFileId: string | null;
  bannerFileId: string | null;
  heroMessage: string | null;
  ctaButtonText: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Update catalog configuration DTO
 */
export interface UpdateCatalogConfigDto {
  slug?: string;
  heroMessage?: string;
  ctaButtonText?: string;
  logoFileId?: string;
  bannerFileId?: string;
}
